import { useTranslation } from 'react-i18next';
import type { UploadProgress } from '../../hooks/usePhotoUploads';
import { Icon, Upload } from './Icon';

/** Pinned to the top: the toasts own the bottom, and errors arrive mid-batch. */
export default function UploadProgressBar({ progress }: { progress: UploadProgress | null }) {
  const { t } = useTranslation();
  if (!progress) return null;

  const { total, done, current } = progress;
  const pct = Math.min(100, Math.round(((done + current) / total) * 100));

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-3 left-4 right-4 md:left-auto md:w-80 z-[90] rounded-xl bg-white dark:bg-slate-800 shadow-lg ring-1 ring-slate-200 dark:ring-slate-700 px-4 py-3 animate-fade-in"
    >
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
          <Icon as={Upload} size={14} />
          {t('upload.progress.title', { current: Math.min(done + 1, total), total })}
        </span>
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{pct}%</span>
      </div>
      <div
        className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="h-full rounded-full bg-teal-600 transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{t('upload.progress.keepOpen')}</p>
    </div>
  );
}
